import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { AlertTriangle, ArrowLeft, CalendarCheck, CheckCircle2, Loader2, Search, ShoppingCart, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui'
import {
  cancelPlannedStudioPurchase,
  completeStudioPurchase,
  fetchStudioPurchaseCandidates,
  fetchStudioPurchaseContext,
  fetchStudioPurchasePartners,
  planStudioPurchase,
  type StudioPurchaseCandidate,
  type StudioPurchaseContext,
  type StudioPurchasePartner,
} from '@/services/studioPurchaseService'

export type StudioPurchaseMode = 'plan' | 'complete'

const PHASE_LABELS: Record<string, string> = {
  BUYING: 'зашёл на покупку',
  OWNED: 'владеет',
  FOR_SALE: 'на продаже',
  PLANNED: 'планируется к открытию',
  SOLD: 'продана',
  TRANSFERRED: 'передана УК',
  NOT_OPENED: 'не открылась',
  CLOSED: 'закрыта',
  SWITCH_TO_PURCHASE: 'переход на покупку',
  TRANSFER: 'передача',
}

const STATUS_LABELS: Record<string, string> = {
  CLOSED: 'закрыта',
  OPEN: 'открыта',
  SUSPENDED: 'приостановлена',
  CANCELLED: 'отменена',
  'PRE-OPEN': 'открывается',
  SWITCH_TO_PURCHASE: 'переход на покупку',
  SERVICE: 'нет продаж',
}

const INPUT_CLASS =
  'mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary'

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function shiftDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(date.getTime())) return isoDate
  date.setDate(date.getDate() + days)
  return date.toISOString().slice(0, 10)
}

function studioLabel(candidate: StudioPurchaseCandidate): string {
  const number = candidate.studio_number ?? candidate.studio_internal_id
  return `№${number} · ${candidate.city_name ?? 'город не указан'}`
}

function partnerLabel(partner: StudioPurchasePartner): string {
  const fio = partner.persons_fio.trim() || 'без персон'
  return `№${partner.partner_number} · ${fio}`
}

function matchesQuery(haystack: Array<string | number | null | undefined>, query: string): boolean {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) return true
  return haystack
    .filter(value => value !== null && value !== undefined && value !== '')
    .join(' ')
    .toLowerCase()
    .includes(trimmed)
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right font-medium text-foreground">{value}</span>
    </div>
  )
}

export function StudioPurchaseModal({
  initialMode = 'plan',
  onBack,
  onClose,
  onDone,
}: {
  initialMode?: StudioPurchaseMode
  onBack?: () => void
  onClose: () => void
  onDone?: () => void
}) {
  const [mode, setMode] = useState<StudioPurchaseMode>(initialMode)

  const [candidates, setCandidates] = useState<StudioPurchaseCandidate[]>([])
  const [partners, setPartners] = useState<StudioPurchasePartner[]>([])
  const [referencesLoading, setReferencesLoading] = useState(true)

  const [studioQuery, setStudioQuery] = useState('')
  const [partnerQuery, setPartnerQuery] = useState('')
  const [studioInternalId, setStudioInternalId] = useState<number | null>(null)
  const [buyerPartnerNumber, setBuyerPartnerNumber] = useState<number | null>(null)

  const [context, setContext] = useState<StudioPurchaseContext | null>(null)
  const [contextLoading, setContextLoading] = useState(false)

  const [transferDate, setTransferDate] = useState('')
  const [transferDateTouched, setTransferDateTouched] = useState(false)
  const [useExistingBuying, setUseExistingBuying] = useState(true)
  const [sellerExitsNetwork, setSellerExitsNetwork] = useState(false)
  const [note, setNote] = useState('')

  const [saving, setSaving] = useState(false)
  const [cancellingId, setCancellingId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function loadReferences() {
      setReferencesLoading(true)
      setError(null)
      try {
        const [candidateRows, partnerRows] = await Promise.all([
          fetchStudioPurchaseCandidates(),
          fetchStudioPurchasePartners(),
        ])
        if (cancelled) return
        setCandidates(candidateRows)
        setPartners(partnerRows)
      } catch (loadError: unknown) {
        if (!cancelled) setError(errorMessage(loadError, 'Не удалось загрузить справочники'))
      } finally {
        if (!cancelled) setReferencesLoading(false)
      }
    }

    void loadReferences()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function loadContext() {
      if (studioInternalId === null) {
        setContext(null)
        return
      }

      setContextLoading(true)
      try {
        const next = await fetchStudioPurchaseContext(studioInternalId, buyerPartnerNumber)
        if (cancelled) return
        setContext(next)
      } catch (loadError: unknown) {
        if (!cancelled) {
          setContext(null)
          setError(errorMessage(loadError, 'Не удалось загрузить данные студии'))
        }
      } finally {
        if (!cancelled) setContextLoading(false)
      }
    }

    void loadContext()
    return () => {
      cancelled = true
    }
  }, [studioInternalId, buyerPartnerNumber])

  const selectedCandidate = useMemo(
    () => candidates.find(row => row.studio_internal_id === studioInternalId) ?? null,
    [candidates, studioInternalId]
  )

  const currentOwner = context?.open_ownerships[0] ?? null
  const pendingBuyings = useMemo(() => context?.pending_buyings ?? [], [context])
  const buyerPendingBuying = useMemo(
    () => pendingBuyings.find(row => row.partner_number === buyerPartnerNumber) ?? null,
    [pendingBuyings, buyerPartnerNumber]
  )

  // Дата передачи по умолчанию — сегодня по Москве (её отдаёт RPC вместе с контекстом).
  useEffect(() => {
    if (!context?.business_date || transferDateTouched) return
    setTransferDate(current => current || context.business_date)
  }, [context?.business_date, transferDateTouched])

  // Заранее созданное владение BUYING есть — по умолчанию проводим покупку через него.
  useEffect(() => {
    setUseExistingBuying(Boolean(buyerPendingBuying))
  }, [buyerPendingBuying])

  // Продавец без других активных студий — кандидат в «бывшие», предлагаем галку.
  useEffect(() => {
    setSellerExitsNetwork(currentOwner ? currentOwner.owner_other_active_studios === 0 : false)
  }, [currentOwner])

  const filteredCandidates = useMemo(() => {
    return candidates
      .filter(row =>
        matchesQuery(
          [row.studio_number, row.city_name, row.address, row.owner_partner_number, row.owner_persons_fio],
          studioQuery
        )
      )
      .slice(0, 60)
  }, [candidates, studioQuery])

  const filteredPartners = useMemo(() => {
    return partners
      .filter(row => matchesQuery([row.partner_number, row.persons_fio, row.cities_text, row.current_status], partnerQuery))
      .slice(0, 60)
  }, [partners, partnerQuery])

  const warnings = useMemo(() => {
    const list: string[] = []
    if (!context) return list

    if (context.buyer && !context.buyer.exists) {
      list.push('Такого франчайзи нет в справочнике — нового франчайзи заводят отдельным онбордингом.')
    }
    if (context.buyer?.owns_this_studio) {
      list.push('Этот франчайзи уже владеет студией.')
    }
    if (mode === 'complete') {
      if (!currentOwner) {
        list.push('У студии нет активного владения — передавать нечего.')
      }
      if ((context.open_ownerships?.length ?? 0) > 1) {
        list.push('У студии несколько активных владений — сначала разберите пересечение.')
      }
      if (!buyerPendingBuying && pendingBuyings.length > 0) {
        list.push('Запланированные покупки есть, но не у выбранного франчайзи — проверьте покупателя.')
      }
      if (!buyerPendingBuying) {
        list.push('Заранее созданного владения BUYING нет: серия и владение будут созданы сразу в фазе OWNED.')
      }
      if (currentOwner && transferDate && currentOwner.start_date && transferDate <= currentOwner.start_date) {
        list.push('Дата передачи не позже начала владения продавца.')
      }
      if (context.buyer && !context.buyer.has_shmp_group_in_city) {
        list.push('У покупателя нет группы ШМП в этом городе — она будет заведена автоматически.')
      }
    }
    if (mode === 'plan' && buyerPendingBuying) {
      list.push('Покупка этой студии выбранным франчайзи уже запланирована.')
    }
    return list
  }, [context, currentOwner, buyerPendingBuying, pendingBuyings.length, mode, transferDate])

  const canSubmit =
    studioInternalId !== null &&
    buyerPartnerNumber !== null &&
    !saving &&
    (mode === 'plan' || Boolean(transferDate))

  async function reloadContext() {
    if (studioInternalId === null) return
    try {
      setContext(await fetchStudioPurchaseContext(studioInternalId, buyerPartnerNumber))
    } catch {
      /* сообщение уже показано вызывающим действием */
    }
    try {
      setCandidates(await fetchStudioPurchaseCandidates())
    } catch {
      /* справочник обновится при следующем открытии */
    }
  }

  async function handlePlan() {
    if (studioInternalId === null || buyerPartnerNumber === null) return

    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const result = await planStudioPurchase({ studioInternalId, buyerPartnerNumber, note })
      setSuccess(
        `Покупка запланирована: владение ${result.ownership_id} в фазе BUYING, серия ${result.series_id}. `
        + 'Договор и условия роялти заводятся отдельно на странице серии.'
      )
      setNote('')
      await reloadContext()
      onDone?.()
    } catch (planError: unknown) {
      setError(errorMessage(planError, 'Не удалось запланировать покупку'))
    } finally {
      setSaving(false)
    }
  }

  async function handleComplete() {
    if (studioInternalId === null || buyerPartnerNumber === null || !transferDate) return

    const buyingOwnershipId = useExistingBuying ? buyerPendingBuying?.ownership_id ?? null : null
    const sellerLine = currentOwner
      ? `№${currentOwner.partner_number} ${currentOwner.persons_fio || ''}`.trim()
      : 'продавец не определён'

    const confirmed = window.confirm(
      `Провести покупку студии ${selectedCandidate ? studioLabel(selectedCandidate) : studioInternalId}?\n\n`
      + `Продавец ${sellerLine} закрывается ${formatDate(shiftDays(transferDate, -1))} (включительно), `
      + `покупатель №${buyerPartnerNumber} начинает ${formatDate(transferDate)}.`
      + (sellerExitsNetwork ? '\nПродавец будет помечен «бывшим».' : '')
    )
    if (!confirmed) return

    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const result = await completeStudioPurchase({
        studioInternalId,
        buyerPartnerNumber,
        transferDate,
        buyingOwnershipId,
        sellerExitsNetwork,
        note,
      })
      setSuccess(
        `Покупка проведена. Продавец №${result.seller_partner_number} закрыт ${formatDate(result.seller_end_date)}, `
        + `покупатель №${result.buyer_partner_number} владеет с ${formatDate(result.transfer_date)} `
        + `(владение ${result.buyer_ownership_id}, серия ${result.buyer_series_id}). `
        + `Офферов снято: ${result.closed_offers}.`
        + (result.shmp_group_created ? ' Заведена группа ШМП.' : '')
        + (result.seller_marked_former ? ' Продавец помечен «бывшим».' : '')
      )
      setNote('')
      await reloadContext()
      onDone?.()
    } catch (completeError: unknown) {
      setError(errorMessage(completeError, 'Не удалось провести покупку'))
    } finally {
      setSaving(false)
    }
  }

  async function handleCancelPlanned(ownershipId: number) {
    if (!window.confirm('Отменить запланированную покупку? Владение BUYING и пустая серия будут удалены.')) return

    setCancellingId(ownershipId)
    setError(null)
    setSuccess(null)
    try {
      const result = await cancelPlannedStudioPurchase({ ownershipId })
      setSuccess(
        `Планируемая покупка отменена (владение ${result.ownership_id}`
        + `${result.series_dropped ? `, серия ${result.series_id} удалена` : ''}).`
      )
      await reloadContext()
      onDone?.()
    } catch (cancelError: unknown) {
      setError(errorMessage(cancelError, 'Не удалось отменить запланированную покупку'))
    } finally {
      setCancellingId(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div
        className="my-4 w-full max-w-3xl space-y-3 rounded-xl bg-background p-4 shadow-lg"
        onClick={event => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                title="Вернуться к выбору кейса"
                aria-label="Вернуться к выбору кейса"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <h3 className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
              <ShoppingCart size={15} className="shrink-0" />
              Покупка студии
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Закрыть"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex gap-1">
          {(['plan', 'complete'] as const).map(value => (
            <Button
              key={value}
              size="sm"
              variant={mode === value ? 'primary' : 'outline'}
              onClick={() => {
                setMode(value)
                setError(null)
                setSuccess(null)
              }}
            >
              {value === 'plan' ? 'Планирую покупку' : 'Провожу покупку'}
            </Button>
          ))}
        </div>

        <p className="text-xs text-muted-foreground">
          {mode === 'plan'
            ? 'Заводит владение в фазе BUYING без даты начала и без пустого договора. Фаза BUYING не биллится, '
              + 'договор и условия роялти создаются отдельно на странице серии.'
            : 'Закрывает владение продавца днём до передачи (включительно), поднимает покупателя с даты передачи, '
              + 'снимает студию с продажи и заводит группу ШМП.'}
        </p>

        {referencesLoading ? (
          <div className="flex items-center gap-2 rounded border border-border bg-muted/30 px-3 py-4 text-sm text-muted-foreground">
            <Loader2 size={15} className="animate-spin" />
            Загружаем студии и франчайзи…
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block text-xs font-medium text-muted-foreground">
              Студия
              <div className="mt-1 flex h-9 items-center gap-1 rounded border border-border bg-background px-2 focus-within:border-primary">
                <Search size={14} className="shrink-0 text-muted-foreground" />
                <input
                  value={studioQuery}
                  onChange={event => setStudioQuery(event.target.value)}
                  placeholder="номер, город, адрес, владелец"
                  className="min-w-0 flex-1 bg-transparent text-sm font-normal text-foreground outline-none"
                />
              </div>
              <select
                value={studioInternalId ?? ''}
                onChange={event => {
                  const value = event.target.value
                  setStudioInternalId(value ? Number(value) : null)
                  setSuccess(null)
                  setError(null)
                }}
                size={6}
                className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
              >
                {filteredCandidates.map(row => (
                  <option key={row.studio_internal_id} value={row.studio_internal_id}>
                    {studioLabel(row)}
                    {row.owner_partner_number ? ` · №${row.owner_partner_number} ${row.owner_persons_fio}` : ' · без владельца'}
                    {row.status_code && row.status_code !== 'OPEN'
                      ? ` · ${STATUS_LABELS[row.status_code] ?? row.status_code}`
                      : ''}
                    {row.has_active_offer ? ' · на продаже' : ''}
                    {row.pending_buying_count > 0 ? ` · план: ${row.pending_buying_count}` : ''}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
                Показано {filteredCandidates.length} из {candidates.length}
              </span>
            </label>

            <label className="block text-xs font-medium text-muted-foreground">
              Покупатель
              <div className="mt-1 flex h-9 items-center gap-1 rounded border border-border bg-background px-2 focus-within:border-primary">
                <Search size={14} className="shrink-0 text-muted-foreground" />
                <input
                  value={partnerQuery}
                  onChange={event => setPartnerQuery(event.target.value)}
                  placeholder="номер франчайзи, ФИО, город"
                  className="min-w-0 flex-1 bg-transparent text-sm font-normal text-foreground outline-none"
                />
              </div>
              <select
                value={buyerPartnerNumber ?? ''}
                onChange={event => {
                  const value = event.target.value
                  setBuyerPartnerNumber(value ? Number(value) : null)
                  setSuccess(null)
                  setError(null)
                }}
                size={6}
                className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
              >
                {filteredPartners.map(row => (
                  <option key={row.partner_number} value={row.partner_number}>
                    {partnerLabel(row)}
                    {row.current_status ? ` · ${row.current_status}` : ''}
                    {row.active_studio_count ? ` · студий: ${row.active_studio_count}` : ''}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
                Показано {filteredPartners.length} из {partners.length}
              </span>
            </label>
          </div>
        )}

        {studioInternalId !== null && (
          <div className="rounded-lg border border-border bg-muted/20 p-3 text-xs">
            {contextLoading && !context ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 size={14} className="animate-spin" />
                Подтягиваем данные студии…
              </div>
            ) : context ? (
              <div className="grid grid-cols-1 gap-x-6 gap-y-1 md:grid-cols-2">
                <div>
                  <div className="mb-1 font-semibold text-foreground">Кого закрываем</div>
                  {currentOwner ? (
                    <>
                      <InfoRow
                        label="Продавец"
                        value={`№${currentOwner.partner_number} ${currentOwner.persons_fio || ''}`.trim()}
                      />
                      <InfoRow label="Владеет с" value={formatDate(currentOwner.start_date)} />
                      <InfoRow
                        label="Фаза"
                        value={PHASE_LABELS[currentOwner.ownership_phase] ?? currentOwner.ownership_phase}
                      />
                      <InfoRow label="Останется студий" value={currentOwner.owner_other_active_studios} />
                    </>
                  ) : (
                    <div className="text-muted-foreground">Активного владения нет</div>
                  )}
                </div>

                <div>
                  <div className="mb-1 font-semibold text-foreground">Кого поднимаем</div>
                  {context.buyer ? (
                    <>
                      <InfoRow
                        label="Покупатель"
                        value={`№${context.buyer.partner_number} ${context.buyer.persons_fio}`.trim()}
                      />
                      <InfoRow
                        label="Владение BUYING"
                        value={buyerPendingBuying ? `есть, ${buyerPendingBuying.ownership_id}` : 'нет, создадим'}
                      />
                      <InfoRow
                        label="ШМП в городе"
                        value={`${context.studio.city_name ?? 'город не указан'} — ${context.buyer.has_shmp_group_in_city ? 'есть' : 'заведём'}`}
                      />
                      <InfoRow
                        label="Снимем с продажи"
                        value={context.active_offer_count > 0 ? `офферов: ${context.active_offer_count}` : 'не продаётся'}
                      />
                    </>
                  ) : (
                    <div className="text-muted-foreground">Покупатель не выбран</div>
                  )}
                </div>
              </div>
            ) : null}

            {pendingBuyings.length > 0 && (
              <div className="mt-3 border-t border-border pt-2">
                <div className="mb-1 font-semibold text-foreground">Запланированные покупки</div>
                <div className="space-y-1">
                  {pendingBuyings.map(row => (
                    <div
                      key={row.ownership_id}
                      className="flex items-center justify-between gap-2 rounded border border-border bg-background px-2 py-1.5"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium text-foreground">
                          №{row.partner_number} {row.persons_fio}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          владение {row.ownership_id} · серия {row.series_id} · заведено {formatDate(row.created_at)}
                          {row.series_has_contracts ? ' · есть договор' : ''}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {row.partner_number !== buyerPartnerNumber && (
                          <Button size="sm" variant="outline" onClick={() => setBuyerPartnerNumber(row.partner_number)}>
                            Выбрать
                          </Button>
                        )}
                        <button
                          type="button"
                          onClick={() => void handleCancelPlanned(row.ownership_id)}
                          disabled={cancellingId === row.ownership_id || row.series_has_contracts}
                          title={row.series_has_contracts ? 'В серии уже есть договор' : 'Отменить планирование'}
                          className="inline-flex h-7 w-7 items-center justify-center rounded border border-border text-muted-foreground hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                        >
                          {cancellingId === row.ownership_id
                            ? <Loader2 size={13} className="animate-spin" />
                            : <Trash2 size={13} />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {mode === 'complete' && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="text-xs font-medium text-muted-foreground">
              Дата передачи
              <input
                type="date"
                value={transferDate}
                onChange={event => {
                  setTransferDate(event.target.value)
                  setTransferDateTouched(true)
                }}
                className={INPUT_CLASS}
              />
              <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
                Продавец закрывается {formatDate(transferDate ? shiftDays(transferDate, -1) : null)} включительно
              </span>
            </label>

            <div className="space-y-2 text-xs text-muted-foreground">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={useExistingBuying}
                  disabled={!buyerPendingBuying}
                  onChange={event => setUseExistingBuying(event.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Использовать запланированную покупку
                  {buyerPendingBuying ? ` (владение ${buyerPendingBuying.ownership_id})` : ' — у покупателя её нет'}
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={sellerExitsNetwork}
                  onChange={event => setSellerExitsNetwork(event.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Продавец уходит из сети (статус «бывший»)
                  {currentOwner && currentOwner.owner_other_active_studios > 0
                    ? ` — у него ещё ${currentOwner.owner_other_active_studios} активных студий`
                    : ''}
                </span>
              </label>
            </div>
          </div>
        )}

        <label className="block text-xs font-medium text-muted-foreground">
          Заметка
          <textarea
            value={note}
            onChange={event => setNote(event.target.value)}
            rows={2}
            placeholder="основание, номер сделки, договорённости"
            className="mt-1 w-full resize-none rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary"
          />
        </label>

        {warnings.length > 0 && (
          <div className="space-y-1 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {warnings.map(item => (
              <div key={item} className="flex items-start gap-1.5">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        {success && (
          <div className="flex items-start gap-1.5 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
            <span>{success}</span>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>
            Закрыть
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={() => void (mode === 'plan' ? handlePlan() : handleComplete())}
          >
            {saving
              ? <Loader2 size={14} className="mr-1 animate-spin" />
              : <CalendarCheck size={14} className="mr-1" />}
            {mode === 'plan' ? 'Запланировать покупку' : 'Провести покупку'}
          </Button>
        </div>
      </div>
    </div>
  )
}
